import React from "react";
import { useList, useInvalidate } from "@/lib/hooks";
import api from "@/lib/api";
import { PageHeader, Panel, StatusBadge, EmptyState, money } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import LoadDocuments from "@/components/LoadDocuments";
import { TimeZones } from "@/components/DashboardWidgets";
import { Truck, MapPin, CalendarBlank, CheckCircle, Package, ShieldWarning, Money, DownloadSimple, Clock, PaperPlaneRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ETAForm({ load, onSaved }) {
  const [eta, setEta] = React.useState(load.eta ? String(load.eta).slice(0, 16) : "");
  const [note, setNote] = React.useState(load.status_note || "");
  const [saving, setSaving] = React.useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/driver/loads/${load.id}`, { eta, status_note: note });
      toast.success("ETA & status shared with dispatch");
      onSaved && onSaved();
    } catch (e) { toast.error("Could not update"); }
    finally { setSaving(false); }
  };
  return (
    <div className="mt-4 bg-blue-50 border border-blue-100 rounded-lg p-3" data-testid={`eta-form-${load.id}`}>
      <p className="text-xs font-bold uppercase tracking-wide text-blue-700 mb-2 flex items-center gap-1.5"><Clock size={14} />Share ETA & Status</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Input type="datetime-local" value={eta} onChange={(e) => setEta(e.target.value)} className="bg-white border-slate-300" data-testid={`eta-input-${load.id}`} />
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. running 1h late, I-40 traffic" className="bg-white border-slate-300" data-testid={`eta-note-${load.id}`} />
      </div>
      <Button onClick={save} disabled={saving} className="mt-2 bg-blue-600 text-white hover:bg-blue-700 font-semibold" data-testid={`eta-save-${load.id}`}>
        <PaperPlaneRight size={15} weight="fill" className="mr-1.5" />{saving ? "Sharing…" : "Share Update"}
      </Button>
    </div>
  );
}

function ComplianceBanner({ compliance = [] }) {
  const flagged = compliance.filter((c) => c.days_left != null && c.days_left <= 30);
  if (!flagged.length) return null;
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5" data-testid="driver-compliance-banner">
      <div className="flex items-center gap-2 mb-2">
        <ShieldWarning size={20} weight="duotone" className="text-amber-600" />
        <p className="font-heading font-bold text-amber-800">Document attention needed</p>
      </div>
      <ul className="space-y-1">
        {flagged.map((c, i) => (
          <li key={i} className="text-sm text-amber-700 capitalize">
            {String(c.type || "").replace(/_/g, " ")} {c.days_left < 0 ? "expired" : `expires in ${c.days_left} days`} ({c.expiry_date})
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function DriverPortal() {
  const { data: loads = [], isLoading, refetch } = useList("driver-loads", "/driver/loads", { refetchInterval: 20000 });
  const { data: profile } = useList("driver-profile", "/driver/profile");
  const { data: settlements = [] } = useList("driver-settlements", "/driver/settlements");
  const invalidate = useInvalidate();

  const setStatus = async (l, status) => {
    try {
      await api.put(`/driver/loads/${l.id}`, { status });
      toast.success(status === "active" ? "Marked picked up" : "Marked delivered");
      refetch();
      invalidate(["driver-loads"]);
    } catch (e) {
      toast.error("Could not update status");
    }
  };

  const activeLoads = loads.filter((l) => l.status !== "completed");
  const doneLoads = loads.filter((l) => l.status === "completed");

  return (
    <div className="max-w-3xl mx-auto">
      <PageHeader title="My Loads" subtitle={profile?.name ? `Driver · ${profile.name}` : "Your assigned trips"} />
      <div className="mb-6"><TimeZones /></div>
      <ComplianceBanner compliance={profile?.compliance} />

      {isLoading ? (
        <p className="text-slate-400">Loading your loads…</p>
      ) : loads.length === 0 ? (
        <Panel><EmptyState icon={Truck} title="No loads assigned" hint="Your dispatcher will assign loads to you here." /></Panel>
      ) : (
        <div className="space-y-4">
          {[...activeLoads, ...doneLoads].map((l) => (
            <Panel key={l.id} className="p-4" >
              <div data-testid={`driver-load-${l.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-mono text-sm text-slate-500">{l.reference}</p>
                    <p className="font-heading font-bold text-slate-900 flex items-center gap-1.5 mt-0.5">
                      <MapPin size={16} className="text-blue-600" />{l.origin} <span className="text-slate-300">→</span> {l.destination}
                    </p>
                  </div>
                  <StatusBadge status={l.status} />
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-sm">
                  <div>
                    <p className="text-xs text-slate-400 flex items-center gap-1"><CalendarBlank size={13} />Pickup</p>
                    <p className="text-slate-700 font-medium">{l.pickup_date || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 flex items-center gap-1"><CalendarBlank size={13} />Delivery</p>
                    <p className="text-slate-700 font-medium">{l.delivery_date || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 flex items-center gap-1"><Package size={13} />Equipment</p>
                    <p className="text-slate-700 font-medium">{l.equipment || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Rate</p>
                    <p className="text-slate-700 font-mono font-semibold">{money(l.rate)}</p>
                  </div>
                </div>

                {l.notes && <p className="text-sm text-slate-500 mt-3 bg-slate-50 rounded-lg p-2">{l.notes}</p>}

                <div className="flex flex-wrap gap-2 mt-4">
                  {l.status === "pending" && (
                    <Button onClick={() => setStatus(l, "active")} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold" data-testid={`pickup-btn-${l.id}`}>
                      <Truck size={16} className="mr-1.5" />Mark Picked Up
                    </Button>
                  )}
                  {l.status === "active" && (
                    <Button onClick={() => setStatus(l, "completed")} className="bg-emerald-600 text-white hover:bg-emerald-700 font-semibold" data-testid={`deliver-btn-${l.id}`}>
                      <CheckCircle size={16} className="mr-1.5" />Mark Delivered
                    </Button>
                  )}
                </div>

                {l.status !== "completed" && <ETAForm load={l} onSaved={refetch} />}

                <div className="mt-4 pt-4 border-t border-slate-100">
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">Documents (BOL / POD)</p>
                  <LoadDocuments loadId={l.id} canUpload canDelete={false} allowCamera />
                </div>
              </div>
            </Panel>
          ))}
        </div>
      )}

      {settlements.length > 0 && (
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-3">
            <Money size={20} weight="duotone" className="text-blue-600" />
            <h2 className="font-heading font-bold text-slate-900">Pay Statements</h2>
          </div>
          <Panel className="p-0">
            <div className="divide-y divide-slate-100" data-testid="driver-settlements">
              {settlements.map((s) => (
                <div key={s.id} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50" data-testid={`driver-settlement-${s.id}`}>
                  <div>
                    <p className="text-sm font-medium text-slate-800">{s.period || "Settlement"}</p>
                    <p className="text-xs text-slate-400">{s.load_count != null ? `${s.load_count} loads · ` : ""}gross {money(s.gross_pay)} · net <span className="font-semibold text-slate-700">{money(s.net_pay)}</span></p>
                  </div>
                  <a
                    href={`${API}/settlements/${s.id}/pdf`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-700"
                    data-testid={`settlement-pdf-${s.id}`}
                  >
                    <DownloadSimple size={16} />PDF
                  </a>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}
    </div>
  );
}
